import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import isString from "lodash/isString";
import { useMemo } from "react";
import { getDocumentTypeOptions } from "../../config/fileDocGroupAndTypes";
import LoanDocumentGroupIcon from "../../screens/LoanDrilldownScreen/shared/LoanDocumentGroupIcon";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";

const SelectFileAssignmentInput = ({
  onChange,
  sx,
  docGroup,
  docType,
  size = "medium",
  placeholder,
  onBlur,
  error,
  helperText,
}) => {
  const options = useMemo(() => {
    const opt = getDocumentTypeOptions();
    return opt;
  }, []);

  const selectedOption = useMemo(() => {
    if (!docGroup || !docType) return null;

    return (
      options?.find((option) => {
        const [optGroup, optType] = option.label.split(" > ");
        return optGroup === docGroup && optType === docType;
      }) || null
    );
  }, [docGroup, docType, options]);

  const displayValue = useMemo(() => {
    if (docGroup && docType) {
      return `${docGroup} > ${docType}`;
    }
    if (docGroup && !docType) {
      return docGroup;
    }
    return "";
  }, [docGroup, docType]);

  const styles = useMemo(() => {
    return {
      ".MuiAutocomplete-endAdornment": {
        marginRight: "0px",
      },
      "& .MuiInputBase-input": {
        color: displayValue ? "inherit" : "rgba(0, 0, 0, 0.7)",
      },

      ...sx,
    };
  }, [sx, displayValue]);

  const emptyPlaceholderValue = useMemo(() => {
    if (placeholder) return placeholder;
    return "Start Typing...";
  }, [placeholder]);

  const populatedPlaceholderValue = useMemo(() => {
    if (placeholder) return `Add ${placeholder}...`;
    return "Start Typing...";
  }, [placeholder]);

  return (
    <Autocomplete
      loading={isNil(options) || isEmpty(options)}
      value={selectedOption}
      onBlur={onBlur}
      onChange={(event, newValue) => {
        if (!newValue) {
          onChange({ docGroup: null, docType: null });
          return;
        }

        if (isString(newValue)) {
          onChange({ docGroup: newValue, docType: null });
        } else {
          const [group, type] = newValue.label.split(" > ");
          onChange({ docGroup: group, docType: type });
        }
      }}
      isOptionEqualToValue={(option, val) => {
        if (!option || !val) return false;
        if (isString(option) || isString(val)) {
          return option === val;
        }
        return option.label === val.label;
      }}
      size={size}
      options={options}
      sx={styles}
      slotProps={{
        popper: {
          sx: {
            "& .MuiPaper-root": {
              minWidth: "520px !important",
            },
          },
        },
      }}
      getOptionLabel={(option) => {
        if (!option) return "";
        if (isString(option)) return option;

        if (option === selectedOption && displayValue) {
          return displayValue;
        }

        return option.label || "";
      }}
      freeSolo={false}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;

        if (isString(option)) {
          return (
            <Box key={option} {...optionProps}>
              {option}
            </Box>
          );
        }

        const [group, type] = option.label.split(" > ");

        return (
          <Box
            key={option.value || option.label}
            column
            sx={{
              justifyContent: "flex-start",
              width: "100%",
              borderBottom: `1px solid #e0e0e0`,
            }}
            {...optionProps}
          >
            <Flx ac fw gap={2} sx={{ py: 0.5 }}>
              <LoanDocumentGroupIcon styled docGroup={group} size={28} />

              <Flx fw ac wrap>
                <Txt secondary>{group}</Txt>
                <ChevronRightRounded className="thin-8" />
                <Txt bold>{type}</Txt>
              </Flx>
            </Flx>
          </Box>
        );
      }}
      renderInput={(params) => {
        const customParams = {
          ...params,
          inputProps: {
            ...params.inputProps,
            ...(selectedOption && displayValue ? { value: displayValue } : {}),
          },
        };

        return (
          <TextField
            {...customParams}
            placeholder={
              displayValue ? populatedPlaceholderValue : emptyPlaceholderValue
            }
            helperText={helperText}
            error={error}
          />
        );
      }}
    />
  );
};

export default SelectFileAssignmentInput;
